/* eslint-disable @typescript-eslint/no-explicit-any */
import useLayoutEffect from './useLayoutEffect'
import {
  type Attributes,
  Children,
  type ReactNode,
  type RefObject,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

function usePreviousRef<T>(value: T) {
  const valueRef = useRef<T | undefined>(undefined)
  useEffect(() => {
    valueRef.current = value
  }, [value])
  return valueRef
}

type ViableReactNode = Exclude<
  ReactNode,
  null | string | undefined | boolean | number | Iterable<ReactNode> | bigint | Promise<any>
>
type Rect = { top: number; parentTop: number }

function calculateBoundingBoxes(
  children: ReactNode | undefined,
  elementsMap: Map<string, HTMLElement | null>,
  parentTop: number
) {
  const boundingBoxes = new Map<string, Rect>()
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return

    if (!child.key) return
    const node = elementsMap.get(child.key)
    if (!node) return
    const top = node.getBoundingClientRect()?.top
    const box = { top, parentTop }
    boundingBoxes.set(child.key, box)
  })

  return boundingBoxes
}

/**
 *
 * @param key - raw react child key
 * @returns normalised key, without the markings of a specific array it's used in
 */
function normaliseChildKey(key: string | null) {
  if (key === null) return ''
  return key.replace('.$', '')
}

function concatChildren(base: ViableReactNode[], extension: ViableReactNode[]) {
  const seen = new Set<string | null>()
  const result: ViableReactNode[] = []

  for (const item of base) {
    const value = item.key
    if (!seen.has(value)) {
      seen.add(value)
      result.push(item)
    }
  }

  for (const item of extension) {
    const value = item.key
    if (!seen.has(value)) {
      seen.add(value)
      result.push(item)
    }
  }

  return result
}

const clipPathVisibleStyle = 'inset(0)'
const clipPathHiddenStyle = 'inset(0 0 100% 0)'

function AnimateList({
  children,
  animationLengthInMs: animLenInMs,
  tagToRenderSpy: Tag = 'div',
}: {
  children: ViableReactNode[]
  animationLengthInMs: number
  tagToRenderSpy?: 'p' | 'span' | 'div' | 'li' | 'tr' | 'td'
}) {
  const spyRef = useRef<HTMLElement>(null)
  const buildTransitionValue = useCallback(
    (transform?: number, clipPath?: number, opacity?: number) => {
      return `transform ${transform ?? animLenInMs}ms ease, clip-path ${clipPath ?? animLenInMs}ms ease, opacity ${opacity ?? animLenInMs}ms ease`
    },
    [animLenInMs]
  )

  const [boundingBoxes, setBoundingBoxes] = useState(new Map<string, Rect>())
  const prevChildrenRef = usePreviousRef(children)
  const prevBoundingBoxes = usePreviousRef(boundingBoxes)
  const [exitingChildren, setExitingChildren] = useState<ViableReactNode[]>([])
  const elementsRef = useRef<Map<string, HTMLElement | null>>(new Map())

  useLayoutEffect(() => {
    const parentTop = spyRef.current?.parentElement?.getBoundingClientRect().top ?? 0
    setBoundingBoxes(calculateBoundingBoxes(children, elementsRef.current, parentTop))
    const prevChildren = prevChildrenRef.current
    // Find children that are being removed
    if (!prevChildren) return
    const currentKeys = new Set(
      Children.map(children, (child) => (isValidElement(child) ? normaliseChildKey(child.key) : null))
    )
    const exiting = Children.toArray(prevChildren).filter(
      (child) => isValidElement(child) && !currentKeys.has(normaliseChildKey(child.key))
    ) as ViableReactNode[]
    if (!exiting.length) return
    setExitingChildren((prev) => concatChildren(prev, exiting))
  }, [children, prevChildrenRef, spyRef])

  const allChildren = useMemo(
    () =>
      concatChildren(Children.toArray(children) as ViableReactNode[], exitingChildren).filter((child) => {
        if (!isValidElement(child)) {
          console.warn('AnimateList got an invalid child')
          return false
        }
        if (!child.key) {
          console.warn('AnimateList got a child with no key, skipping')
          return false
        }
        return true
      }),
    [children, exitingChildren]
  )

  useLayoutEffect(() => {
    let newlyAppearedCount = 0

    const animationFrames: number[] = []
    const timeouts: NodeJS.Timeout[] = []
    const abortCtrl = new AbortController()

    for (const child of allChildren) {
      if (!isValidElement(child)) continue
      if (!child.key) continue
      const normalisedKey = normaliseChildKey(child.key)
      const box = boundingBoxes.get(normalisedKey)
      const prevBox = prevBoundingBoxes.current?.get(normalisedKey)
      const domNode = elementsRef.current.get(normalisedKey)
      if (!domNode) continue

      if (!box) {
        // Element is disappearing, animate clip-path from full height to 0 height
        animationFrames.push(
          requestAnimationFrame(() => {
            domNode.style.transition = buildTransitionValue()
            if (domNode.style.transform !== clipPathHiddenStyle) {
              domNode.style.clipPath = clipPathVisibleStyle
              domNode.style.opacity = '1'
            }

            animationFrames.push(
              requestAnimationFrame(() => {
                domNode.style.clipPath = clipPathHiddenStyle
                domNode.style.opacity = '0'
              })
            )
          })
        )

        // Remove from exitingChildren after animation completes
        domNode.addEventListener(
          'transitionend',
          function listener(evt) {
            if (evt.propertyName !== 'clip-path') return
            setExitingChildren((prev) =>
              prev.filter(
                (exitingChild) => isValidElement(exitingChild) && normaliseChildKey(exitingChild.key) !== normalisedKey
              )
            )
            elementsRef.current.delete(normalisedKey)
            abortCtrl.abort()
          },
          { signal: abortCtrl.signal }
        )

        continue
      }

      // We need to check if the element's animation was interrupted by an update
      if (!prevBox || domNode.style.opacity === '0') {
        // Element is appearing, animate clip-path from 0 height to full height with stagger
        const staggerDelay = Math.round((newlyAppearedCount * animLenInMs) / 2)
        newlyAppearedCount++

        animationFrames.push(
          requestAnimationFrame(() => {
            domNode.style.clipPath = clipPathHiddenStyle
            domNode.style.opacity = '0'

            domNode.style.transition = buildTransitionValue()

            timeouts.push(
              setTimeout(() => {
                animationFrames.push(
                  requestAnimationFrame(() => {
                    domNode.style.clipPath = clipPathVisibleStyle
                    domNode.style.opacity = '1'
                  })
                )
              }, staggerDelay)
            )
          })
        )

        continue
      }

      const deltaY = prevBox.top - prevBox.parentTop - (box.top - box.parentTop)

      if (!deltaY) continue
      animationFrames.push(
        requestAnimationFrame(() => {
          domNode.style.transform = `translateY(${deltaY}px)`
          domNode.style.transition = buildTransitionValue(0)

          animationFrames.push(
            requestAnimationFrame(() => {
              domNode.style.transition = buildTransitionValue()
              domNode.style.transform = 'translateY(0)'
            })
          )
        })
      )
    }

    return () => {
      timeouts.map(clearTimeout)
      animationFrames.map(cancelAnimationFrame)
      if (!abortCtrl.signal.aborted) abortCtrl.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animLenInMs, boundingBoxes, buildTransitionValue, allChildren])

  return (
    <>
      <Tag ref={spyRef as RefObject<any>} style={{ maxBlockSize: 0 }}></Tag>
      {allChildren.map((child) => {
        // Clone the element and attach a ref
        return cloneElement(child, {
          ref: (node: HTMLElement | null) => {
            elementsRef.current.set(normaliseChildKey(child.key), node)
          },
        } as Attributes)
      })}
    </>
  )
}

export default AnimateList
